// @ts-nocheck
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import { Search } from 'lucide-react';
import CoinProfileModal from '../components/CoinProfileModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

interface Coin {
  symbol: string;
  name: string;
  logo_url?: string;
  current_price?: number;
  market_cap?: number;
}

const SearchCoin = () => {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [allCoins, setAllCoins] = useState<Coin[]>([]);
  const [filteredCoins, setFilteredCoins] = useState<Coin[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [top10Coins, setTop10Coins] = useState<Coin[]>([]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchAllCoins();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCoins(allCoins);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = allCoins.filter(
        coin =>
          coin.symbol.toLowerCase().includes(query) ||
          coin.name?.toLowerCase().includes(query)
      );
      setFilteredCoins(filtered);
    }
  }, [searchQuery, allCoins]);

  const fetchAllCoins = async () => {
    try {
      setLoading(true);

      // Fetch from Bull Market Support Band USD to get market cap data
      const bmsbResponse = await axios.get(`${API_BASE}/api/bull-market-support-band-usd?limit=500`, {
        headers: getAuthHeaders()
      });

      console.log('SearchCoin: BMSB Response:', bmsbResponse.data);

      if (bmsbResponse.data) {
        const bmsbData = Array.isArray(bmsbResponse.data) ? bmsbResponse.data : bmsbResponse.data.data || [];

        const coins = bmsbData.map((coin: any) => ({
          symbol: coin.symbol,
          name: coin.name || coin.symbol,
          logo_url: coin.logo_url,
          current_price: coin.current_price_usd || coin.current_price,
          market_cap: coin.market_cap || 0
        }));

        console.log('SearchCoin: Mapped coins:', coins.length);
        console.log('SearchCoin: Sample coin data:', coins.slice(0, 3));

        setAllCoins(coins);
        setFilteredCoins(coins);

        // Get top 10 by market cap
        const sortedByMarketCap = [...coins]
          .filter(coin => coin.market_cap && coin.market_cap > 0)
          .sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0))
          .slice(0, 10);

        console.log('SearchCoin: Top 10 coins:', sortedByMarketCap);
        setTop10Coins(sortedByMarketCap);
      } else {
        console.warn('SearchCoin: No BMSB data in response');
      }
    } catch (error) {
      console.error('Error fetching coins:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCoinLogoUrl = (coin: Coin) => {
    if (coin.logo_url) return coin.logo_url;
    return `https://assets.coincap.io/assets/icons/${coin.symbol.toLowerCase()}@2x.png`;
  };

  const handleCoinClick = (symbol: string) => {
    setSelectedCoin(symbol);
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="flex items-center justify-center min-h-[60vh]">
              <div className="text-center">
                <div className={`inline-block animate-spin rounded-full h-12 w-12 border-b-2 ${isDark ? 'border-emerald-400' : 'border-orange-600'}`}></div>
                <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading coins...</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {selectedCoin && (
        <CoinProfileModal
          symbol={selectedCoin}
          onClose={() => setSelectedCoin(null)}
        />
      )}
      <div className={`min-h-screen ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-amber-600 to-yellow-600 opacity-10"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

          <div className="relative max-w-7xl mx-auto px-6 py-12">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-4">
                <span className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 bg-clip-text text-transparent">
                  Search
                </span>
                <br />
                <span className={isDark ? 'text-white' : 'text-gray-900'}>Coins</span>
              </h1>
              <p className={`text-lg mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Find detailed analysis for any cryptocurrency
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/20">
                <Search className="w-4 h-4 text-orange-500" />
                <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {allCoins.length}+ Cryptocurrencies
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="px-4 sm:px-0">

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              </div>
              <input
                type="text"
                placeholder="Search by symbol or name (e.g., BTC, Bitcoin, Ethereum...)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-12 pr-4 py-4 rounded-xl border text-lg transition-colors ${
                  isDark
                    ? 'bg-white/[0.03] border-white/10 text-white placeholder-gray-500 focus:border-emerald-500/50 focus:bg-white/[0.05]'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-orange-500 focus:ring-orange-500'
                } focus:outline-none focus:ring-2 focus:ring-offset-0`}
              />
            </div>
          </div>

          {/* Top 10 Coins by Market Cap */}
          {searchQuery === '' && top10Coins.length > 0 && (
            <div className="mb-8">
              <h2 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Top 10 by Market Cap
              </h2>
              <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Click any coin to view detailed analysis
              </p>
              <div className="flex flex-wrap gap-3">
                {top10Coins.map((coin, index) => (
                  <button
                    key={coin.symbol}
                    onClick={() => handleCoinClick(coin.symbol)}
                    className={`group relative px-5 py-3 rounded-full border-2 transition-all duration-300 transform hover:scale-105 hover:shadow-lg ${
                      isDark
                        ? 'bg-gradient-to-r from-white/[0.05] to-white/[0.08] border-white/20 hover:border-emerald-500/60 hover:bg-white/[0.12]'
                        : 'bg-white border-gray-300 hover:border-orange-500 hover:shadow-orange-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {/* Rank Badge */}
                      <div className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                        isDark
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-orange-100 text-orange-600'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Coin Logo */}
                      <img
                        src={getCoinLogoUrl(coin)}
                        alt={coin.symbol}
                        className={`w-8 h-8 rounded-full ring-2 ring-offset-1 transition-all group-hover:ring-4 ${
                          isDark
                            ? 'ring-emerald-500/30 group-hover:ring-emerald-500/50'
                            : 'ring-orange-300 group-hover:ring-orange-400'
                        }`}
                        onError={(e) => {
                          e.currentTarget.src = 'https://via.placeholder.com/32';
                        }}
                      />

                      {/* Coin Symbol */}
                      <div className="flex flex-col items-start">
                        <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          {coin.symbol}
                        </div>
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          ${(coin.market_cap / 1e9).toFixed(1)}B
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search Results - Only show when user is actively searching */}
          {searchQuery && (
            <div>
              <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Search Results ({filteredCoins.length})
              </h2>

              {filteredCoins.length === 0 ? (
                <div className={`text-center py-12 rounded-xl border ${
                  isDark ? 'bg-white/[0.03] border-white/10' : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className={`text-5xl mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>🔍</div>
                  <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    No coins found
                  </h3>
                  <p className={`mt-2 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Try searching with a different term
                  </p>
                </div>
              ) : (
                <div className={`rounded-xl border overflow-hidden ${
                  isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
                }`}>
                  <div className="max-h-[600px] overflow-y-auto">
                    <div className={`divide-y ${isDark ? 'divide-white/10' : 'divide-gray-200'}`}>
                      {filteredCoins.map((coin) => (
                        <button
                          key={coin.symbol}
                          onClick={() => handleCoinClick(coin.symbol)}
                          className={`w-full px-6 py-4 flex items-center justify-between transition-colors ${
                            isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center space-x-4">
                            <img
                              src={getCoinLogoUrl(coin)}
                              alt={coin.symbol}
                              className="w-10 h-10 rounded-full"
                              onError={(e) => {
                                e.currentTarget.src = 'https://via.placeholder.com/40';
                              }}
                            />
                            <div className="text-left">
                              <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {coin.symbol}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                {coin.name}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            {coin.current_price && (
                              <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                ${coin.current_price.toLocaleString()}
                              </div>
                            )}
                            {coin.market_cap && (
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                MCap: ${(coin.market_cap / 1e9).toFixed(2)}B
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SearchCoin;
